<template>
    <div>
        <!-- Namenska raba in podrobni prostorski izvedbeni pogoji -->
        <h3>{{ $t("aa:municipality:acts") }}</h3>
        <table class="table table-condensed">
            <template v-if="formattedDedicatedUsages.length > 0">
                <tbody>
                    <!-- Header row -->
                    <tr>
                        <th colspan="7">{{ $t("spatial_acts:dedicated_usage_detailed_spatial_implementation_conditions") }}</th>
                    </tr>
                    <tr>
                        <th>{{ $t("aa:for_property") }}</th>
                        <th>{{ $t("general:description") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_tag") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_detailed_tag") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_level") }}</th>
                        <th>{{ $t("spatial_acts:act_number") }}</th>
                    </tr>
                    <!-- Data rows -->
                    <tr v-for="(act, inx) in formattedDedicatedUsages" :key="`act_${inx}`">
                        <td>{{ act.properties.join(", ") }}</td>
                        <td>{{ act.POD_NAM_RABA_NAZIV }}</td>
                        <td>{{ act.POD_NAM_RABA_OZNAKA }}</td>
                        <td>{{ act.POD_NAM_RABA_NAZIV_DODATEK }}</td>
                        <td>{{ act.POD_NAM_RABA_RAVEN }}</td>
                        <td>{{ act.STEVILKA }}</td>
                    </tr>
                </tbody>
            </template>
        </table>

        <!-- Spatial planning acts table -->
        <h3 class="m-t-40">{{ $t("aa:spatial_planning") }}</h3>
        <table class="table table-condensed">
            <tbody class="thead">
                <tr>
                    <th>{{ $t("spatial_acts:act_number") }}</th>
                    <th>{{ $t("general:name") }}</th>
                    <th>{{ $t("spatial_acts:date_accepted") }}</th>
                    <th>{{ $t("spatial_acts:date_published") }}</th>
                    <th>{{ $t("spatial_acts:date_validity_start") }}</th>
                    <th v-if="eupMap">{{ $t("spatial_acts:spatial_planning_unit_tag") }}</th>
                </tr>
            </tbody>

            <tbody v-for="(sectionActs, sectionTitle) in filteredActs" :key="`section_${sectionTitle}`">
                <tr>
                    <th colspan="6">{{ getTranslation(sectionTitle) }}</th>
                </tr>

                <tr v-for="(act, inx) in sectionActs" :key="`act_${sectionTitle}_${inx}`">
                    <td>{{ act.STEVILKA }}</td>
                    <td>
                        <a v-if="act.POSTOPEK_EPL_ID" :href="getPostopekHref(act)" target="_blank">
                            {{ act.NAZIV_AKTA }}
                        </a>
                        <template v-else>
                            {{ act.NAZIV_AKTA }}
                        </template>
                    </td>
                    <td>{{ act.DATUM_SPREJEMA }}</td>
                    <td>{{ act.DATUM_OBJAVE }}</td>
                    <td>{{ act.DATUM_ZAC_VELJAVNOSTI }}</td>
                    <td v-if="eupMap">
                        <template v-if="eupMap[act.STEVILKA]">
                            {{ eupMap[act.STEVILKA].join(", ") }}
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Combined tables for each real estate -->
        <div v-for="propertyKey in propertyKeys" :key="`property_${propertyKey}`">
            <h3 class="m-t-40">{{ $t("spatial_acts:property_overview") }}: {{ propertyKey }}</h3>

            <!-- Spatial acts selection table for this property -->
            <table class="table table-condensed">
                <tbody>
                    <tr>
                        <th colspan="7">{{ $t("aa:spatial_planning") }}</th>
                    </tr>
                    <tr>
                        <th colspan="4">{{ $t("general:name") }}</th>
                        <th>{{ $t("spatial_acts:act_number") }}</th>
                        <th>{{ $t("spatial_acts:coverage_percentage") }}</th>
                        <th>{{ $t("spatial_acts:relative_m2") }}</th>
                    </tr>
                    <tr v-for="(act, inx) in spatialActsByProperty[propertyKey] || []" :key="`spatial_act_${propertyKey}_${inx}`">
                        <td colspan="4">{{ getTranslation(act.sectionTitle) }}</td>
                        <td>{{ act.STEVILKA }}</td>
                        <td>
                            <template v-if="act.intersection_percentage === null || act.intersection_percentage <= 0">/</template>
                            <template v-else>{{ act.intersection_percentage|formatPercent(false, 2) }}</template>
                        </td>
                        <td>
                            <template v-if="act.coverage_m2 === null || act.coverage_m2 <= 0">/</template>
                            <template v-else>{{ act.coverage_m2|formatNumber(2) }} m²</template>
                        </td>
                    </tr>
                </tbody>
                <tbody>
                    <tr><th colspan="7">{{ $t("real_estate:parcel:usage") }}</th></tr>
                    <tr>
                        <th>{{ $t("general:description") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_tag") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_detailed_tag") }}</th>
                        <th>{{ $t("spatial_acts:dedicated_usage_level") }}</th>
                        <th>{{ $t("spatial_acts:act_number") }}</th>
                        <th>{{ $t("spatial_acts:coverage_percentage") }}</th>
                        <th>{{ $t("spatial_acts:relative_m2") }}</th>
                    </tr>
                    <tr v-for="(act, inx) in adjustedGroupedPropertyData[propertyKey] || []" :key="`adjusted_act_${propertyKey}_${inx}`">
                        <td>{{ act.description }}</td>
                        <td>{{ act.code }}</td>
                        <td>{{ act.name }}</td>
                        <td>{{ act.level }}</td>
                        <td>{{ act.STEVILKA }}</td>
                        <td>
                            <template v-if="act.intersection_percentage === null">/</template>
                            <template v-else>{{ act.intersection_percentage|formatPercent(false, 2) }}</template>
                        </td>
                        <td>
                            <template v-if="act.relative_m2 === null">/</template>
                            <template v-else>{{ act.relative_m2|formatNumber(2) }} m²</template>
                        </td>
                    </tr>
                    <!-- Sum row -->
                    <tr class="sum-row">
                        <th colspan="5">{{ $t("general:total") }}</th>
                        <th>{{ getPropertyTotal(adjustedGroupedPropertyData[propertyKey] || [])|formatPercent(false, 2) }}</th>
                        <th>{{ getPropertyTotalM2(adjustedGroupedPropertyData[propertyKey] || [])|formatNumber(2) }} m²</th>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import spatialactsmixin from "./spatialactsmixin";

export default {
    props: ["acts", "title"],
    mixins: [spatialactsmixin],
    
    computed: {
        filteredActs() {
            const filteredActs = {};
            for (const act of this.acts) {
                for (const key in act.result["acts"]) {
                    if (act.result["acts"][key].length > 0) {
                        filteredActs[key] = act.result["acts"][key];
                    }
                }
            }
            return filteredActs;
        },
        eupMap() {
            const map = {};
            for (const act of this.acts) {
                for (const eup of act.result.opn_additional["eup"]) {
                    if (!map[eup.STEVILKA])
                        map[eup.STEVILKA] = [eup.NAZIV];    
                    else
                        map[eup.STEVILKA].push(eup.NAZIV);
                }
            }
            return map;
        },
        formattedDedicatedUsages() {
            const additionalInfoByReKey = {};
            
            for (const entry of this.acts) {
                const re_key = entry.property_analysis.real_estate.re_key;
                
                for (const value of entry.result.opn_additional["nam_rab_ppip"]) {
                    const key = `${value.STEVILKA}_${value.POD_NAM_RABA_NAZIV_DODATEK}`
                    if (!additionalInfoByReKey[key]) {
                        value["properties"] = [re_key];
                        additionalInfoByReKey[key] = value;
                    }
                    else if (!additionalInfoByReKey[key]["properties"].includes(re_key)) {
                        additionalInfoByReKey[key]["properties"].push(re_key);
                    }
                }
            }

            return Object.values(additionalInfoByReKey);
        },
        groupedPropertyData() {
            const grouped = {};
            
            for (const entry of this.acts) {
                const re_key = entry.property_analysis.real_estate.re_key;
                const property_size = entry.property_analysis.size || 0;
                
                if (!grouped[re_key]) {
                    grouped[re_key] = [];
                }
                
                // Add namenska raba data
                for (const value of entry.result.opn_additional["nam_rab_ppip"]) {
                    grouped[re_key].push({
                        ...value,
                        property_key: re_key,
                        property_size: property_size,
                        relative_m2: this.calculateRelativeM2(value.intersection_percentage, property_size),
                        type: "Namenska raba",
                        code: value.POD_NAM_RABA_OZNAKA,
                        name: value.POD_NAM_RABA_NAZIV_DODATEK,
                        level: value.POD_NAM_RABA_RAVEN,
                        description: value.POD_NAM_RABA_NAZIV
                    });
                }
                
                // Add spatial acts data (PLAN_EVT_OBMOCJA_PA)
                for (const key in entry.result["acts"]) {
                    for (const act of entry.result["acts"][key]) {
                        if (act.intersection_percentage && act.intersection_percentage > 0) {
                            grouped[re_key].push({
                                ...act,
                                property_key: re_key,
                                property_size: property_size,
                                relative_m2: this.calculateRelativeM2(act.intersection_percentage, property_size),
                                type: "Prostorski akt",
                                code: act.VRSTA_PROSTORSKEGA_AKTA,
                                name: act.NAZIV_AKTA,
                                level: act.STATUS === "V_PRIPRAVI" ? "V pripravi" : "Zaključen",
                                description: act.NAZIV_AKTA
                            });
                        }
                    }
                }
            }

            // Sum identical rows and sort by type, then by name
            for (const propertyKey in grouped) {
                const summedData = this.sumIdenticalRows(grouped[propertyKey]);
                summedData.sort((a, b) => {
                    // Sort by type first, then by name
                    if (a.type !== b.type) {
                        return a.type.localeCompare(b.type);
                    }
                    return (a.name || "").localeCompare(b.name || "");
                });
                grouped[propertyKey] = summedData;
            }

            return grouped;
        },
        spatialActsByProperty() {
            const grouped = {};
            
            for (const entry of this.acts) {
                const re_key = entry.property_analysis.real_estate.re_key;
                const property_size = this.parseNumber(entry.property_analysis.size) || 0;
                
                if (!grouped[re_key]) {
                    grouped[re_key] = [];
                }
                
                // Process spatial acts (PLAN_EVT_OBMOCJA_PA)
                for (const key in entry.result["acts"]) {
                    for (const act of entry.result["acts"][key]) {
                        if (act.intersection_percentage && act.intersection_percentage > 0 && act.intersection_percentage < 100) {
                            grouped[re_key].push({
                                ...act,
                                property_key: re_key,
                                property_size: property_size,
                                coverage_m2: this.parseNumber(act.intersection_area) || 0,
                                sectionTitle: key,
                            });
                        }
                    }
                }
            }
            
            return grouped;
        },
        propertyKeys() {
            const keys = new Set();
            for (const entry of this.acts) {
                keys.add(entry.property_analysis.real_estate.re_key);
            }
            return Array.from(keys);
        },
        adjustedGroupedPropertyData() {
            const grouped = {};

            for (const entry of this.acts) {
                const re_key = entry.property_analysis.real_estate.re_key;
                const property_size = this.parseNumber(entry.property_analysis.size) || 0;

                if (!grouped[re_key]) {
                    grouped[re_key] = [];
                }

                // Add namenska raba data with adjusted calculations
                for (const value of entry.result.opn_additional["nam_rab_ppip"]) {
                    if (value.intersection_percentage && value.intersection_percentage > 0) {
                        const originalPercentage = this.parseNumber(value.intersection_percentage);
                        const originalArea = this.parseNumber(value.intersection_area) || 0;

                        // Calculate what percentage this represents of the remaining available area
                        let adjustedRelativeM2 = 0;
                        if (property_size > 0) {
                            adjustedRelativeM2 = (originalArea / property_size);
                        }

                        grouped[re_key].push({
                            ...value,
                            property_key: re_key,
                            property_size: property_size,
                            adjusted_property_size: property_size,
                            original_area: originalArea,
                            intersection_percentage: originalPercentage, // Keep original percentage
                            relative_m2: adjustedRelativeM2, // Recalculate based on remaining area
                            type: "Namenska raba",
                            code: value.POD_NAM_RABA_OZNAKA,
                            name: value.POD_NAM_RABA_NAZIV_DODATEK,
                            level: value.POD_NAM_RABA_RAVEN,
                            description: value.POD_NAM_RABA_NAZIV
                        });
                    }
                }
            }

            // Sum identical rows and sort by type, then by name
            for (const propertyKey in grouped) {
                const summedData = this.sumIdenticalRows(grouped[propertyKey]);
                summedData.sort((a, b) => {
                    // Sort by type first, then by name
                    if (a.type !== b.type) {
                        return a.type.localeCompare(b.type);
                    }
                    return (a.name || "").localeCompare(b.name || "");
                });
                

                
                grouped[propertyKey] = summedData;
            }

            return grouped;
        }
    },
    methods: {
        parseNumber(value) {
            if (value === null || value === undefined) return 0;
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
                // Remove any non-numeric characters except decimal point and minus
                const cleaned = value.replace(/[^\d.-]/g, '');
                const parsed = parseFloat(cleaned);
                return isNaN(parsed) ? 0 : parsed;
            }
            return 0;
        },
        getPropertyTotal(propertyData) {
            const total = propertyData.reduce((sum, act) => {
                return sum + (act.intersection_percentage || 0);
            }, 0);
            return total;
        },
        getPropertyTotalM2(propertyData) {
            const total = propertyData.reduce((sum, act) => {
                return sum + (act.relative_m2 || 0);
            }, 0);
            return total;
        },
        calculateRelativeM2(percentage, propertySize) {
            if (percentage === null || percentage === undefined || propertySize === 0) {
                return null;
            }
            // Calculate with improved precision
            const result = (percentage / 100) * propertySize;
            // Round to 2 decimal places for consistency with backend
            return Math.round(result * 100) / 100;
        },
        sumIdenticalRows(rows) {
            const grouped = {};
            
            for (const row of rows) {
                // Create a key based on all fields except intersection_percentage and relative_m2
                const key = `${row.type}_${row.code}_${row.name}_${row.level}_${row.description}_${row.STEVILKA}_${row.property_key}`;
                
                if (!grouped[key]) {
                    grouped[key] = { ...row };
                } else {
                    // Sum the percentages (since we're keeping original percentages)
                    const currentPct = grouped[key].intersection_percentage || 0;
                    const newPct = row.intersection_percentage || 0;
                    const summedPct = currentPct + newPct;
                    
                    // Round percentage to 2 decimal places for consistency
                    grouped[key].intersection_percentage = Math.round(summedPct * 100) / 100;
                    
                    // Recalculate relative_m2 based on the summed percentage and adjusted property size
                    if (grouped[key].adjusted_property_size > 0) {
                        const recalculatedM2 = (grouped[key].intersection_percentage / 100) * grouped[key].adjusted_property_size;
                        grouped[key].relative_m2 = Math.round(recalculatedM2 * 100) / 100;
                    }
                }
            }
            
            return Object.values(grouped);
        }
    }
}
</script>

<style scoped>
/* Avoid "fat" border */
.table > tbody + tbody {
    border-top: none;
}
.border-top {
    border-top-width: 2px;
}
.upper {
    text-transform: uppercase;
}
h3 {
    margin-left: 8px;
}
h4 {
    margin-left: 16px;
    margin-top: 20px;
    margin-bottom: 10px;
}
.sum-row {
    background-color: #f8f9fa;
    font-weight: bold;
}
</style>
